import React, { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Plus, X, GripVertical, ChevronRight, ChevronLeft, Check, ArrowUp, ArrowDown, Clock, Loader2, XCircle, Sparkles } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const STEPS = [
  { num: 1, title: 'Write', desc: 'List your morning routine' },
  { num: 2, title: 'Grade', desc: 'Rate each action' },
  { num: 3, title: 'Remove & Reorder', desc: 'Optimize your routine' },
  { num: 4, title: 'Identity Shift', desc: 'Old vs new you' },
  { num: 5, title: 'Commit', desc: 'Lock in your routine' },
];

function SortableItem({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {children({ listeners, isDragging })}
    </div>
  );
}

// Step 1: Write
function WriteStep({ items, setItems }) {
  const [inputVal, setInputVal] = useState('');
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(KeyboardSensor));

  const addItem = () => {
    const text = inputVal.trim();
    if (!text) return;
    setItems(prev => [...prev, { id: `item-${Date.now()}`, text, grade: null, section: 'before', position: prev.length }]);
    setInputVal('');
  };

  const removeItem = (id) => setItems(prev => prev.filter(i => i.id !== id));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setItems(prev => {
        const oldIndex = prev.findIndex(i => i.id === active.id);
        const newIndex = prev.findIndex(i => i.id === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-[--hc-text] mb-1">Step 1: Write Your Morning</h2>
      <p className="text-sm text-[--hc-text-muted] mb-6 leading-relaxed">
        Write down everything you do from the moment you wake up until the moment you start working your business. Do not skip anything. This is awareness, not judgment.
      </p>

      <div className="flex gap-2 mb-4">
        <input
          data-testid="onboarding-write-add-item-input"
          type="text"
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addItem()}
          className="flex-1 px-3 py-2.5 rounded-xl border border-[--hc-border] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[--hc-ring]"
          placeholder="e.g. Wake up, Make coffee, Check phone..."
        />
        <button
          data-testid="onboarding-write-add-item-button"
          onClick={addItem}
          className="px-4 py-2.5 rounded-xl bg-[--hc-primary] text-white text-sm font-medium hover:bg-[--hc-primary-hover] active:scale-[0.98] transition-colors flex items-center gap-1.5"
        >
          <Plus size={16} /> Add
        </button>
      </div>

      {items.length === 0 && (
        <div className="text-center py-8 text-[--hc-text-muted] text-sm">
          <p className="mb-2">Start by adding your first morning activity.</p>
          <p className="text-xs">Examples: Wake up, Check phone, Make coffee, Shower, Eat breakfast</p>
        </div>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2" data-testid="onboarding-write-items-list">
            {items.map((item, idx) => (
              <SortableItem key={item.id} id={item.id}>
                {({ listeners }) => (
                  <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-[--hc-border] bg-white hover:border-[--hc-primary]/20 transition-colors group">
                    <button {...listeners} className="cursor-grab text-[--hc-text-muted] hover:text-[--hc-text] touch-none">
                      <GripVertical size={16} />
                    </button>
                    <span className="text-xs text-[--hc-text-muted] w-5 tabular-nums">{idx + 1}.</span>
                    <span className="flex-1 text-sm text-[--hc-text]">{item.text}</span>
                    <button
                      onClick={() => removeItem(item.id)}
                      className="text-[--hc-text-muted] hover:text-[--hc-danger] opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
              </SortableItem>
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

// Step 2: Grade
function GradeStep({ items, setItems }) {
  const setGrade = (id, grade) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, grade } : i));
  };

  const allGraded = items.every(i => i.grade !== null);

  return (
    <div>
      <h2 className="text-xl font-semibold text-[--hc-text] mb-1">Step 2: Grade Each Action</h2>
      <p className="text-sm text-[--hc-text-muted] mb-6 leading-relaxed">
        Rate each action: does it help (+), hurt (-), or have no impact (/) on getting to your operating time?
      </p>

      <div className="space-y-2">
        {items.map((item) => (
          <div key={item.id} className="flex items-center gap-3 px-3 py-3 rounded-xl border border-[--hc-border] bg-white" data-testid={`onboarding-grade-item-${item.id}`}>
            <span className="flex-1 text-sm text-[--hc-text]">{item.text}</span>
            <div className="flex gap-1.5">
              <button
                data-testid={`grade-plus-${item.id}`}
                onClick={() => setGrade(item.id, '+')}
                className={`w-9 h-9 rounded-lg text-sm font-bold transition-colors ${
                  item.grade === '+' ? 'bg-[--hc-success-soft] text-[--hc-success] ring-2 ring-[--hc-success]/30' : 'bg-[--hc-surface] text-[--hc-text-muted] hover:bg-[--hc-success-soft] hover:text-[--hc-success]'
                }`}
              >+</button>
              <button
                data-testid={`grade-neutral-${item.id}`}
                onClick={() => setGrade(item.id, '/')}
                className={`w-9 h-9 rounded-lg text-sm font-bold transition-colors ${
                  item.grade === '/' ? 'bg-[--hc-surface-2] text-[--hc-text] ring-2 ring-[--hc-text-muted]/30' : 'bg-[--hc-surface] text-[--hc-text-muted] hover:bg-[--hc-surface-2]'
                }`}
              >/</button>
              <button
                data-testid={`grade-minus-${item.id}`}
                onClick={() => setGrade(item.id, '-')}
                className={`w-9 h-9 rounded-lg text-sm font-bold transition-colors ${
                  item.grade === '-' ? 'bg-[--hc-danger-soft] text-[--hc-danger] ring-2 ring-[--hc-danger]/30' : 'bg-[--hc-surface] text-[--hc-text-muted] hover:bg-[--hc-danger-soft] hover:text-[--hc-danger]'
                }`}
              >-</button>
            </div>
          </div>
        ))}
      </div>

      {!allGraded && (
        <p className="text-xs text-[--hc-warning] mt-4">Grade all items before proceeding.</p>
      )}
    </div>
  );
}

// Step 3: Remove & Reorder
function RemoveReorderStep({ items, setItems }) {
  const positiveItems = items.filter(i => i.grade === '+' && i.section === 'before');
  const neutralItems = items.filter(i => i.grade === '/' && i.section === 'before');
  const negativeItems = items.filter(i => i.grade === '-' && i.section === 'before');
  const afterItems = items.filter(i => i.section === 'after');

  const moveToAfter = (id) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, section: 'after' } : i));
  };

  const moveToBefore = (id) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, section: 'before' } : i));
  };

  const moveUp = (id) => {
    setItems(prev => {
      const beforeItems = prev.filter(i => i.section === 'before');
      const afterItemsList = prev.filter(i => i.section === 'after');
      const idx = beforeItems.findIndex(i => i.id === id);
      if (idx > 0) {
        const newBefore = [...beforeItems];
        [newBefore[idx - 1], newBefore[idx]] = [newBefore[idx], newBefore[idx - 1]];
        return [...newBefore, ...afterItemsList];
      }
      return prev;
    });
  };

  const moveDown = (id) => {
    setItems(prev => {
      const beforeItems = prev.filter(i => i.section === 'before');
      const afterItemsList = prev.filter(i => i.section === 'after');
      const idx = beforeItems.findIndex(i => i.id === id);
      if (idx < beforeItems.length - 1) {
        const newBefore = [...beforeItems];
        [newBefore[idx], newBefore[idx + 1]] = [newBefore[idx + 1], newBefore[idx]];
        return [...newBefore, ...afterItemsList];
      }
      return prev;
    });
  };

  const renderGroup = (title, groupItems, color, canRemove = false, canMoveAfter = false) => (
    <div className="mb-4">
      <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
        <span className={`w-5 h-5 rounded flex items-center justify-center text-xs font-bold ${
          color === 'green' ? 'bg-[--hc-success-soft] text-[--hc-success]' :
          color === 'gray' ? 'bg-[--hc-surface-2] text-[--hc-text-muted]' :
          'bg-[--hc-danger-soft] text-[--hc-danger]'
        }`}>
          {color === 'green' ? '+' : color === 'gray' ? '/' : '-'}
        </span>
        {title} ({groupItems.length})
      </h3>
      {groupItems.length === 0 && <p className="text-xs text-[--hc-text-muted] pl-7">None</p>}
      <div className="space-y-1.5">
        {groupItems.map((item) => (
          <div key={item.id} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[--hc-border] bg-white">
            <div className="flex flex-col gap-0.5">
              <button onClick={() => moveUp(item.id)} className="text-[--hc-text-muted] hover:text-[--hc-text]"><ArrowUp size={12} /></button>
              <button onClick={() => moveDown(item.id)} className="text-[--hc-text-muted] hover:text-[--hc-text]"><ArrowDown size={12} /></button>
            </div>
            <span className="flex-1 text-sm text-[--hc-text]">{item.text}</span>
            {canMoveAfter && (
              <button
                onClick={() => moveToAfter(item.id)}
                className="text-xs px-2 py-1 rounded-md bg-[--hc-surface] text-[--hc-text-muted] hover:bg-[--hc-warning-soft] hover:text-[--hc-warning] transition-colors"
              >
                Move to After
              </button>
            )}
            {canRemove && (
              <button
                onClick={() => moveToAfter(item.id)}
                className="text-xs px-2 py-1 rounded-md bg-[--hc-danger-soft] text-[--hc-danger] hover:bg-[--hc-danger] hover:text-white transition-colors"
              >
                Remove
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div>
      <h2 className="text-xl font-semibold text-[--hc-text] mb-1">Step 3: Remove & Reorder</h2>
      <p className="text-sm text-[--hc-text-muted] mb-6 leading-relaxed">
        Remove all Negative actions before your operating time. Keep essential Neutral actions. Move delay-creating actions to after your operating time.
      </p>

      <div data-testid="onboarding-group-plus">
        {renderGroup('Positive', positiveItems, 'green')}
      </div>
      <div data-testid="onboarding-group-neutral">
        {renderGroup('Neutral', neutralItems, 'gray', false, true)}
      </div>
      <div data-testid="onboarding-group-minus">
        {renderGroup('Negative', negativeItems, 'red', true)}
      </div>

      {afterItems.length > 0 && (
        <div className="mt-4 p-3 rounded-xl border-2 border-dashed border-[--hc-border] bg-[--hc-surface]/50">
          <h3 className="text-sm font-semibold text-[--hc-text-muted] mb-2">After Operating Time</h3>
          <div className="space-y-1.5">
            {afterItems.map(item => (
              <div key={item.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-[--hc-border]">
                <span className="flex-1 text-sm text-[--hc-text-muted]">{item.text}</span>
                <button
                  onClick={() => moveToBefore(item.id)}
                  className="text-xs px-2 py-1 rounded-md bg-[--hc-surface] text-[--hc-text-muted] hover:bg-[--hc-success-soft] hover:text-[--hc-success] transition-colors"
                >
                  Move Back
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Step 4: Identity Shift
function IdentityShiftStep({ oldHabits, setOldHabits, newHabits, setNewHabits }) {
  const [oldInput, setOldInput] = useState('');
  const [newInput, setNewInput] = useState('');

  const addOldHabit = () => {
    const text = oldInput.trim();
    if (!text) return;
    setOldHabits(prev => [...prev, text]);
    setOldInput('');
  };

  const removeOldHabit = (idx) => {
    setOldHabits(prev => prev.filter((_, i) => i !== idx));
  };

  const addNewHabit = () => {
    const text = newInput.trim();
    if (!text) return;
    setNewHabits(prev => [...prev, text]);
    setNewInput('');
  };

  const removeNewHabit = (idx) => {
    setNewHabits(prev => prev.filter((_, i) => i !== idx));
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-[--hc-text] mb-1">Step 4: Identity Shift</h2>
      <p className="text-sm text-[--hc-text-muted] mb-6 leading-relaxed">
        Real change starts with identity. Write down the habits you are leaving behind and the person you are becoming. These will show on your dashboard every day as a reminder.
      </p>

      {/* Old Habits Section */}
      <div className="mb-6" data-testid="identity-old-habits-section">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-lg bg-[--hc-danger-soft] flex items-center justify-center">
            <XCircle size={14} className="text-[--hc-danger]" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-[--hc-text]">Old Habits — What I'm Leaving Behind</h3>
            <p className="text-xs text-[--hc-text-muted]">Things you want to stop doing. Be specific.</p>
          </div>
        </div>

        <div className="flex gap-2 mb-3">
          <input
            data-testid="identity-old-habit-input"
            type="text"
            value={oldInput}
            onChange={(e) => setOldInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addOldHabit()}
            className="flex-1 px-3 py-2.5 rounded-xl border border-[--hc-border] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[--hc-ring]"
            placeholder="e.g. Hitting snooze 3 times..."
          />
          <button
            data-testid="identity-old-habit-add-button"
            onClick={addOldHabit}
            className="px-3 py-2.5 rounded-xl border border-[--hc-border] text-sm font-medium text-[--hc-text] bg-white hover:bg-[--hc-surface] active:scale-[0.98] transition-colors flex items-center gap-1.5"
          >
            <Plus size={14} /> Add
          </button>
        </div>

        {oldHabits.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[--hc-border] bg-[--hc-surface]/50 px-4 py-5 text-center">
            <p className="text-xs text-[--hc-text-muted]">Examples: Hitting snooze, Scrolling phone in bed, Skipping breakfast, Checking email before priorities</p>
          </div>
        ) : (
          <div className="space-y-1.5" data-testid="identity-old-habits-list">
            {oldHabits.map((habit, idx) => (
              <div key={idx} className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-[--hc-border] bg-white group">
                <span className="w-5 h-5 rounded flex items-center justify-center bg-[--hc-danger-soft] text-[--hc-danger] text-[10px] font-bold flex-shrink-0">-</span>
                <span className="flex-1 text-sm text-[--hc-text]">{habit}</span>
                <button
                  data-testid={`identity-old-habit-remove-${idx}`}
                  onClick={() => removeOldHabit(idx)}
                  className="text-[--hc-text-muted] hover:text-[--hc-danger] opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New Identity Section */}
      <div data-testid="identity-new-habits-section">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-lg bg-[--hc-accent]/15 flex items-center justify-center">
            <Sparkles size={14} className="text-[--hc-accent]" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-[--hc-text]">New Identity — Who I'm Becoming</h3>
            <p className="text-xs text-[--hc-text-muted]">Write identity statements. Start with "I am..." or "I..."</p>
          </div>
        </div>

        <div className="flex gap-2 mb-3">
          <input
            data-testid="identity-new-habit-input"
            type="text"
            value={newInput}
            onChange={(e) => setNewInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addNewHabit()}
            className="flex-1 px-3 py-2.5 rounded-xl border border-[--hc-border] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[--hc-ring]"
            placeholder="e.g. I am a disciplined operator..."
          />
          <button
            data-testid="identity-new-habit-add-button"
            onClick={addNewHabit}
            className="px-3 py-2.5 rounded-xl bg-[--hc-accent] text-[--hc-on-accent] text-sm font-medium hover:brightness-[0.98] active:scale-[0.98] transition-colors flex items-center gap-1.5"
          >
            <Plus size={14} /> Add
          </button>
        </div>

        {newHabits.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[--hc-accent]/30 bg-[--hc-accent-soft]/30 px-4 py-5 text-center">
            <p className="text-xs text-[--hc-text-muted]">Examples: I am a disciplined operator, I start before I feel ready, I build systems not excuses, I invest in my health first</p>
          </div>
        ) : (
          <div className="space-y-1.5" data-testid="identity-new-habits-list">
            {newHabits.map((habit, idx) => (
              <div key={idx} className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-[--hc-accent]/20 bg-[--hc-accent-soft]/30 group">
                <span className="w-5 h-5 rounded flex items-center justify-center bg-[--hc-accent]/15 text-[--hc-accent] flex-shrink-0">
                  <Sparkles size={10} />
                </span>
                <span className="flex-1 text-sm text-[--hc-text] font-medium">{habit}</span>
                <button
                  data-testid={`identity-new-habit-remove-${idx}`}
                  onClick={() => removeNewHabit(idx)}
                  className="text-[--hc-text-muted] hover:text-[--hc-danger] opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {oldHabits.length === 0 && newHabits.length === 0 && (
        <p className="text-xs text-[--hc-text-muted] mt-4 text-center italic">Add at least one habit to each section — this is your commitment to change.</p>
      )}
    </div>
  );
}

// Step 5: Commit
function CommitStep({ items, wakeTime, setWakeTime, startTime, setStartTime, oldHabits, newHabits }) {
  const beforeItems = items.filter(i => i.section === 'before');

  return (
    <div>
      <h2 className="text-xl font-semibold text-[--hc-text] mb-1">Step 5: Commit to Your Routine</h2>
      <p className="text-sm text-[--hc-text-muted] mb-6">Your optimized morning routine is ready. Set your times and lock it in.</p>

      <div className="rounded-xl border border-[--hc-border] bg-[--hc-surface] p-4 mb-6">
        <h3 className="text-sm font-semibold text-[--hc-text] mb-3">Your Morning Routine</h3>
        <div className="space-y-1.5">
          {beforeItems.map((item, idx) => (
            <div key={item.id} className="flex items-center gap-2 text-sm">
              <span className="w-5 text-[--hc-text-muted] tabular-nums text-xs">{idx + 1}.</span>
              <span className="text-[--hc-text]">{item.text}</span>
              <span className={`ml-auto w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold ${
                item.grade === '+' ? 'bg-[--hc-success-soft] text-[--hc-success]' :
                item.grade === '/' ? 'bg-[--hc-surface-2] text-[--hc-text-muted]' :
                'bg-[--hc-danger-soft] text-[--hc-danger]'
              }`}>
                {item.grade}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Identity Shift Summary */}
      {(oldHabits.length > 0 || newHabits.length > 0) && (
        <div className="rounded-xl border border-[--hc-border] bg-[--hc-surface] p-4 mb-6">
          <h3 className="text-sm font-semibold text-[--hc-text] mb-3">Your Identity Shift</h3>
          {oldHabits.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-medium text-[--hc-danger] mb-1.5">Leaving behind:</p>
              <div className="space-y-1">
                {oldHabits.map((h, i) => (
                  <p key={i} className="text-xs text-[--hc-text-muted] line-through pl-3">{h}</p>
                ))}
              </div>
            </div>
          )}
          {newHabits.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[--hc-accent] mb-1.5">Becoming:</p>
              <div className="space-y-1">
                {newHabits.map((h, i) => (
                  <p key={i} className="text-xs text-[--hc-text] font-medium pl-3">{h}</p>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-[--hc-text] mb-1.5 flex items-center gap-1.5">
            <Clock size={14} /> Wake-up Time
          </label>
          <input
            data-testid="onboarding-commit-start-time"
            type="time"
            value={wakeTime}
            onChange={(e) => setWakeTime(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl border border-[--hc-border] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[--hc-ring]"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[--hc-text] mb-1.5 flex items-center gap-1.5">
            <Clock size={14} /> Start Operating
          </label>
          <input
            data-testid="onboarding-commit-end-time"
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl border border-[--hc-border] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[--hc-ring]"
          />
        </div>
      </div>

      <div className="rounded-xl bg-[--hc-accent-soft] border border-[--hc-accent]/20 p-4 text-center">
        <p className="text-sm font-medium text-[--hc-on-accent]">
          Your routine: <span className="font-bold">{beforeItems.length} steps</span>. 
          Wake up at <span className="font-bold">{wakeTime}</span>. 
          Start operating by <span className="font-bold">{startTime}</span>.
        </p>
      </div>
    </div>
  );
}

// Main Onboarding Page
export default function OnboardingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { updateUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [items, setItems] = useState([]);
  const [wakeTime, setWakeTime] = useState('06:00');
  const [startTime, setStartTime] = useState('08:00');
  const [oldHabits, setOldHabits] = useState([]);
  const [newHabits, setNewHabits] = useState([]);
  const [saving, setSaving] = useState(false);

  const isEmbed = searchParams.get('embed') === 'true';

  const canProceed = () => {
    if (currentStep === 1) return items.length >= 1;
    if (currentStep === 2) return items.every(i => i.grade !== null);
    if (currentStep === 3) return items.filter(i => i.section === 'before').length >= 1;
    if (currentStep === 4) return true; // Identity shift is optional but encouraged
    return true;
  };

  const handleCommit = async () => {
    setSaving(true);
    try {
      const routineItems = items.map((item, idx) => ({
        id: item.id,
        text: item.text,
        grade: item.grade,
        position: idx,
        section: item.section,
      }));

      await api.post('/routine/save', {
        items: routineItems,
        wake_time: wakeTime,
        start_operating_time: startTime,
        old_habits: oldHabits,
        new_habits: newHabits,
      });

      // Refresh user data
      const meRes = await api.get('/auth/me');
      updateUser(meRes.data.user);
      toast.success('Routine locked in! Let\'s start operating.');
      navigate(`/dashboard${isEmbed ? '?embed=true' : ''}`);
    } catch (err) {
      toast.error('Failed to save routine. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ─── Embed layout: flush, no logo, horizontal step bar ───
  if (isEmbed) {
    return (
      <div className="min-h-screen bg-[--hc-bg]" data-testid="embed-onboarding">
        {/* Compact horizontal step bar */}
        <div className="sticky top-0 z-30 bg-white border-b border-[--hc-border]">
          <div className="px-5 pt-3 pb-0" data-testid="onboarding-stepper">
            <div className="flex items-center gap-1 mb-3">
              {STEPS.map((step, idx) => (
                <React.Fragment key={step.num}>
                  <button
                    onClick={() => { if (step.num <= currentStep) setCurrentStep(step.num); }}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                      step.num === currentStep
                        ? 'text-[--hc-primary]'
                        : step.num < currentStep
                        ? 'text-[--hc-text-muted] hover:text-[--hc-text]'
                        : 'text-[--hc-text-muted]/50 cursor-default'
                    }`}
                  >
                    <span className={`w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                      step.num === currentStep
                        ? 'bg-[--hc-accent] text-[--hc-on-accent]'
                        : step.num < currentStep
                        ? 'bg-[--hc-primary] text-white'
                        : 'bg-[--hc-surface-2] text-[--hc-text-muted]'
                    }`}>
                      {step.num < currentStep ? <Check size={10} /> : step.num}
                    </span>
                    <span className="hidden sm:inline">{step.title}</span>
                  </button>
                  {idx < STEPS.length - 1 && (
                    <div className={`flex-1 h-px max-w-[40px] ${step.num < currentStep ? 'bg-[--hc-primary]' : 'bg-[--hc-border]'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>
            {/* Progress bar */}
            <div className="w-full h-1 rounded-full bg-[--hc-surface-2] overflow-hidden -mb-px">
              <div
                className="h-full rounded-full bg-[--hc-accent] transition-all duration-300"
                style={{ width: `${((currentStep - 1) / (STEPS.length - 1)) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Content area — flush, no centered container */}
        <div className="px-5 py-4">
          <div>
            {currentStep === 1 && <WriteStep items={items} setItems={setItems} />}
            {currentStep === 2 && <GradeStep items={items} setItems={setItems} />}
            {currentStep === 3 && <RemoveReorderStep items={items} setItems={setItems} />}
            {currentStep === 4 && <IdentityShiftStep oldHabits={oldHabits} setOldHabits={setOldHabits} newHabits={newHabits} setNewHabits={setNewHabits} />}
            {currentStep === 5 && <CommitStep items={items} wakeTime={wakeTime} setWakeTime={setWakeTime} startTime={startTime} setStartTime={setStartTime} oldHabits={oldHabits} newHabits={newHabits} />}
          </div>

          {/* Action bar */}
          <div className="flex justify-between mt-4 pb-2">
            {currentStep > 1 ? (
              <button
                onClick={() => setCurrentStep(prev => prev - 1)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-[--hc-border] text-sm font-medium text-[--hc-text] bg-white hover:bg-[--hc-surface] transition-colors"
              >
                <ChevronLeft size={16} /> Back
              </button>
            ) : <div />}

            {currentStep < 5 ? (
              <button
                onClick={() => canProceed() && setCurrentStep(prev => prev + 1)}
                disabled={!canProceed()}
                className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-[--hc-primary] text-white text-sm font-medium hover:bg-[--hc-primary-hover] active:scale-[0.98] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next <ChevronRight size={16} />
              </button>
            ) : (
              <button
                data-testid="onboarding-commit-button"
                onClick={handleCommit}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2 rounded-xl bg-[--hc-accent] text-[--hc-on-accent] text-sm font-bold hover:brightness-[0.98] active:scale-[0.98] transition-all disabled:opacity-50"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                {saving ? 'Saving...' : 'Lock It In'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── Standard (non-embed) layout ───
  return (
    <div className="min-h-screen bg-[--hc-bg]">
      <div className="max-w-[900px] mx-auto px-4 py-6 md:py-10">
        {/* Header */}
        <div className="mb-8">
          <img src="/logo.png" alt="HabitCloser" className="h-9 w-auto" />
        </div>

        <div className="flex flex-col md:flex-row gap-8">
          {/* Steps rail */}
          <div className="md:w-[220px] flex-shrink-0" data-testid="onboarding-stepper">
            <div className="flex md:flex-col gap-2">
              {STEPS.map(step => (
                <button
                  key={step.num}
                  onClick={() => {
                    if (step.num <= currentStep) setCurrentStep(step.num);
                  }}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-left w-full transition-colors ${
                    step.num === currentStep
                      ? 'bg-[--hc-accent-soft] border border-[--hc-accent]/30'
                      : step.num < currentStep
                      ? 'bg-[--hc-surface] border border-transparent'
                      : 'bg-transparent border border-transparent opacity-50'
                  }`}
                >
                  <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    step.num === currentStep
                      ? 'bg-[--hc-accent] text-[--hc-on-accent]'
                      : step.num < currentStep
                      ? 'bg-[--hc-primary] text-white'
                      : 'bg-[--hc-surface-2] text-[--hc-text-muted]'
                  }`}>
                    {step.num < currentStep ? <Check size={14} /> : step.num}
                  </span>
                  <div className="hidden md:block">
                    <p className="text-sm font-medium text-[--hc-text]">{step.title}</p>
                    <p className="text-xs text-[--hc-text-muted]">{step.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="rounded-xl border border-[--hc-border] bg-white p-5 md:p-6">
              {currentStep === 1 && <WriteStep items={items} setItems={setItems} />}
              {currentStep === 2 && <GradeStep items={items} setItems={setItems} />}
              {currentStep === 3 && <RemoveReorderStep items={items} setItems={setItems} />}
              {currentStep === 4 && <IdentityShiftStep oldHabits={oldHabits} setOldHabits={setOldHabits} newHabits={newHabits} setNewHabits={setNewHabits} />}
              {currentStep === 5 && <CommitStep items={items} wakeTime={wakeTime} setWakeTime={setWakeTime} startTime={startTime} setStartTime={setStartTime} oldHabits={oldHabits} newHabits={newHabits} />}
            </div>

            {/* Action bar */}
            <div className="flex justify-between mt-4">
              {currentStep > 1 ? (
                <button
                  onClick={() => setCurrentStep(prev => prev - 1)}
                  className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-[--hc-border] text-sm font-medium text-[--hc-text] bg-white hover:bg-[--hc-surface] transition-colors"
                >
                  <ChevronLeft size={16} /> Back
                </button>
              ) : <div />}

              {currentStep < 5 ? (
                <button
                  onClick={() => canProceed() && setCurrentStep(prev => prev + 1)}
                  disabled={!canProceed()}
                  className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-[--hc-primary] text-white text-sm font-medium hover:bg-[--hc-primary-hover] active:scale-[0.98] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next <ChevronRight size={16} />
                </button>
              ) : (
                <button
                  data-testid="onboarding-commit-button"
                  onClick={handleCommit}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[--hc-accent] text-[--hc-on-accent] text-sm font-bold hover:brightness-[0.98] active:scale-[0.98] transition-all disabled:opacity-50"
                >
                  {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                  {saving ? 'Saving...' : 'Lock It In'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
